import AgentIcon from "../AgentIcon";
import { AgentChatExtras } from "./AgentChatMessage";
import AgentChatMessage from "./AgentChatMessage";
import ChatDropOverlay from "./ChatDropOverlay";
import ChatEmptyState from "./ChatEmptyState";
import ErrorChatMessage from "./ErrorChatMessage";
import ToolApprovalCard from "./ToolApprovalCard";
import UserChatMessage from "./UserChatMessage";
import type { AgentExtraItem } from "@app/config/agentWebSocket";
import type { ConnectionState } from "@app/config/agentWebSocket";
import posthogEvents from "@app/config/posthog/posthog-events";
import { useFeatureFlag } from "@app/hooks/useFeatureFlag";
import type { AgentChatMode, AgentChatSettings } from "@app/state/types";
import type { ToolApprovalDecision, ToolApprovalRequest } from "@globals";
import { MAX_DOCS_PER_SESSION, admitDraftFiles, remainingDocumentSlots } from "@lib/agent/chatAttachments";
import type { ChatMessage, CommittedDocuments } from "@lib/agent/types";
import type { AgentChatSend } from "@lib/agent/useAgentChat";
import Icon from "@lib/common/components/Icon/Icon";
import { useFileDrop } from "@lib/common/hooks/useFileDrop";
import formatChatDate from "@lib/common/utils/formatChatDate";
import { useScrollPin } from "@lib/experiment/agent/AgentChat/useScrollPin";
import ChatBox, {
  type ChatBoxOnSubmit,
  type ChatBoxSubmitPayload
} from "@lib/experiment/agent/ExperimentDesignChatBox/ChatBox";
import type { DraftReport } from "@lib/research/AttachReportModalContent";
import {
  ActionIcon,
  Box,
  Button,
  Center,
  Group,
  Menu,
  ScrollArea,
  Skeleton,
  Stack,
  Text,
  Tooltip,
  Transition
} from "@mantine/core";
import { usePostHog } from "posthog-js/react";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type AgentChatProps = {
  messages: ChatMessage[];
  extras?: AgentExtraItem[];
  isStreaming: boolean;
  /** True from the click of Stop until the turn actually settles — drives the Stop button's
   * spinner/disabled state while cooperative backend cancellation catches up. */
  isStopping: boolean;
  connectionState: ConnectionState;
  /** Sends a message over the websocket. Used for in-message quick-replies, which never carry
   * attachments — the compose area's upload-then-send path is `onComposerSubmit`. */
  send: AgentChatSend;
  /** Compose-area submit: uploads any draft files, then sends. Returns `false` to keep the draft
   * (e.g. an upload failed) so ChatBox doesn't clear the text and files. */
  onSubmit: ChatBoxOnSubmit;
  /** Draft files held in the compose area, not yet uploaded. */
  selectedFiles: File[];
  onFilesChange: (files: File[]) => void;
  selectedReports?: DraftReport[];
  onReportsChange?: (reports: DraftReport[]) => void;
  /** Documents already committed to the session — count caps the picker slots; totalBytes feeds the
   * cumulative size hint. Both are soft hints: the upload endpoint stays authoritative. */
  committedDocuments: CommittedDocuments;
  stop: VoidFunction;
  /** True while the persisted message history is loading on first mount. */
  isHistoryLoading?: boolean;
  /** True when the history bootstrap query failed; renders an inline retry banner. */
  historyError?: boolean;
  disabledClear?: boolean;
  userFeedback?: ReactNode;
  /** Deep-research run status bar, rendered above the compose area (below the session rating).
   * Carries its own spacing and renders nothing when there is no run to show. */
  researchStatus?: ReactNode;
  onRetryHistory?: () => void;
  onClear: VoidFunction;
  variant?: "draft" | "experiment";
  /** True while session documents are uploading (bootstrap handoff or compose path). */
  isUploading?: boolean;
  composerSeed?: string;
  onComposerSeedConsumed?: () => void;
  /** Not wired on dashboard. */
  onAttachReportClick?: () => void;
  /** Gated tool calls awaiting approval. The turn stays paused until one is answered. */
  pendingApprovals: ToolApprovalRequest[];
  onRespondToApproval: (decisions: ToolApprovalDecision[]) => void;
} & Pick<AgentChatSettings, "chatMode"> & {
    setChatMode: (mode: AgentChatMode) => void;
    toggle: (value: boolean) => void;
  };

export default function AgentChat({
  messages,
  extras,
  pendingApprovals,
  onRespondToApproval,
  isStreaming,
  isStopping,
  connectionState,
  send,
  onSubmit,
  selectedFiles,
  onFilesChange,
  selectedReports,
  onReportsChange,
  committedDocuments,
  stop,
  isHistoryLoading = false,
  historyError = false,
  onRetryHistory,
  onClear,
  disabledClear,
  userFeedback,
  researchStatus,
  variant = "experiment",
  chatMode,
  setChatMode,
  toggle,
  isUploading = false,
  composerSeed,
  onComposerSeedConsumed,
  onAttachReportClick
}: AgentChatProps) {
  const posthog = usePostHog();
  const isReady = connectionState === "connected";
  const isReconnecting = connectionState === "reconnecting";
  const isFileUploadEnabled = useFeatureFlag("agent-file-upload");
  const isDeepResearchEnabled = useFeatureFlag("deep-research");

  const viewportRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<HTMLTextAreaElement>(null);

  const { scrollToBottom: pinToBottom, showScrollToBottom } = useScrollPin(viewportRef, messages);

  const scrollToBottom = useCallback(() => {
    pinToBottom();
    chatInputRef.current?.focus();
  }, [pinToBottom]);

  // The approval card renders below the thread but comes from `pendingApprovals`, which
  // `useScrollPin` does not follow — it watches `messages`. A pause that lands below the fold
  // would read as a turn that simply stopped. This overrides a deliberate scroll-up, which it
  // should: the turn is parked until the card is answered, so there is nothing else to read.
  const hasPendingApprovals = pendingApprovals.length > 0;
  useEffect(() => {
    if (hasPendingApprovals) {
      pinToBottom();
    }
  }, [hasPendingApprovals, pinToBottom]);

  // Scroll to the bottom the first time messages populate so history loads at the end.
  const hasInitialScrolledRef = useRef(false);
  useEffect(() => {
    if (hasInitialScrolledRef.current || messages.length === 0) {
      return;
    }
    hasInitialScrolledRef.current = true;
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Focus chat input when connected
  useEffect(() => {
    if (isReady) {
      chatInputRef.current?.focus();
      scrollToBottom();
    }
  }, [isReady, scrollToBottom]);

  // True while a send is in flight. `isUploading` only covers the upload request itself, so this
  // also spans the moments around it where a dropped file would be captured by the send and then
  // cleared with the draft without ever being uploaded.
  const [isSending, setIsSending] = useState(false);
  const handleOnSubmit = useCallback(
    async (message: string, payload: ChatBoxSubmitPayload) => {
      posthog.capture(posthogEvents.experiment_agent.agent_chat_input_submit, { text_length: message.length });
      setIsSending(true);
      try {
        // Must return (not fire-and-forget) the result: ChatBox awaits it to decide whether to clear
        // the composer, so swallowing it here would always clear the draft even on a failed send.
        return await onSubmit(message, payload);
      } finally {
        setIsSending(false);
      }
    },
    [posthog, onSubmit]
  );

  const handleFilesDropped = useCallback(
    (files: File[]) => {
      posthog.capture(posthogEvents.experiment_agent.file_drop, { file_count: files.length });
      const accepted = admitDraftFiles(files, selectedFiles, committedDocuments);
      if (accepted.length > 0) {
        onFilesChange([...selectedFiles, ...accepted]);
      }
    },
    [posthog, selectedFiles, committedDocuments, onFilesChange]
  );

  // Why a drop would be refused right now, shown on the overlay in place of the allow-list.
  const dropBlockedReason = useMemo(() => {
    if (remainingDocumentSlots(committedDocuments, selectedFiles) <= 0) {
      return `Document limit reached (${MAX_DOCS_PER_SESSION}/session)`;
    }
    if (isSending || isUploading) {
      return "Wait for the current message to send";
    }
    return undefined;
  }, [committedDocuments, selectedFiles, isSending, isUploading]);

  const { isDragging, dropProps } = useFileDrop({
    onDrop: handleFilesDropped,
    disabled: dropBlockedReason !== undefined
  });

  const handleMinimize = useCallback(() => {
    posthog.capture(posthogEvents.experiment_agent.minimize_click);
    toggle(false);
  }, [posthog, toggle]);

  const handleSetChatMode = useCallback(
    (mode: AgentChatMode) => {
      posthog.capture(posthogEvents.experiment_agent.toggle_chat_mode_click, { mode });
      setChatMode(mode);
    },
    [posthog, setChatMode]
  );

  const handleAttachClick = useCallback(() => {
    posthog.capture(posthogEvents.experiment_agent.file_upload_click);
  }, [posthog]);

  const handleAttachDeepResearchReportClick = useCallback(() => {
    posthog.capture(posthogEvents.experiment_agent.attach_deep_research_report_click);
    if (typeof onAttachReportClick === "function") {
      onAttachReportClick();
    }
  }, [posthog]);

  const formattedDate = useMemo(() => formatChatDate(new Date()), []);

  const defaultPlaceholder = variant === "draft" ? "Describe your experiment..." : "Ask about this experiment...";
  // Hint the input with the most recent open question's placeholder, if any. The action check
  // narrows the union, so input_placeholder is reachable without a cast.
  const inputSuggestion = (extras ?? []).reduce<string | null>(
    (acc, item) => (item.action === "question_open" ? (item.input_placeholder ?? null) : acc),
    null
  );

  // `isUploading` isn't folded in here: ChatBox already gates on it internally (re-entry guard,
  // dimmed compose area), so adding it here would just be a second source of truth for the same
  // "busy" state.
  const disabled = !isReady || isHistoryLoading || historyError;
  const showEmptyState = !isHistoryLoading && !historyError && messages.length === 0;

  return (
    <Box pos="relative" h="100%" {...(isFileUploadEnabled ? dropProps : {})}>
      <Stack h="100%" gap={0}>
        <Group px="xs" align="center" gap="md" justify="space-between" py={3} wrap="nowrap">
          <Group gap="xs" wrap="nowrap">
            <AgentIcon size="1rem" />
            <Stack gap={0}>
              <Text size="xs" fw={500} lh={1} truncate>
                AI chat · {formattedDate}
              </Text>
              {isReconnecting && (
                <Text size="xs" c="orange" lh={1.4}>
                  Reconnecting…
                </Text>
              )}
            </Stack>
          </Group>
          <Group gap={5} wrap="nowrap">
            <Tooltip label="Clear conversation" disabled={disabledClear}>
              <ActionIcon onClick={onClear} disabled={disabledClear}>
                <Icon name="trash" />
              </ActionIcon>
            </Tooltip>

            <Menu width={150}>
              <Menu.Target>
                <Tooltip label="Switch chat mode">
                  <ActionIcon>
                    <Icon name="sidebar-flip" />
                  </ActionIcon>
                </Tooltip>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  onClick={() => handleSetChatMode("sidebar")}
                  leftSection={<Icon name="sidebar-flip" color="dimmed" size="0.8rem" />}
                  rightSection={chatMode === "sidebar" ? <Icon name="check" /> : undefined}
                >
                  Sidebar
                </Menu.Item>
                <Menu.Item
                  onClick={() => handleSetChatMode("floating")}
                  leftSection={<Icon name="window-restore" color="dimmed" size="0.8rem" />}
                  rightSection={chatMode === "floating" ? <Icon name="check" /> : undefined}
                >
                  Floating
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>

            <Tooltip label="Hide chat">
              <ActionIcon onClick={handleMinimize}>
                <Icon name="dash" size="0.8rem" />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
        <ScrollArea viewportRef={viewportRef} flex={1} scrollbarSize={2}>
          <Box p="sm" pb="5rem" pr="sm">
            <Stack gap="md" py="md" style={{ display: "flex", flexDirection: "column" }}>
              {historyError && (
                <Center>
                  <Stack gap="sm" align="center" mt="lg" maw={400}>
                    <Text c="dimmed" ta="center">
                      Couldn&apos;t load the conversation history.
                    </Text>
                    {onRetryHistory && (
                      <Button variant="light" size="xs" onClick={onRetryHistory}>
                        Retry
                      </Button>
                    )}
                  </Stack>
                </Center>
              )}
              {isHistoryLoading && (
                <Stack gap="md" py="md">
                  <Skeleton height={48} width="60%" radius="md" />
                  <Skeleton height={64} width="80%" radius="md" ml="auto" />
                  <Skeleton height={48} width="55%" radius="md" />
                </Stack>
              )}
              {showEmptyState && (
                <Center>
                  <Box mt="20vh">
                    <ChatEmptyState isDeepResearchEnabled={isDeepResearchEnabled} />
                  </Box>
                </Center>
              )}
              {messages.map((message, i) => {
                switch (message.role) {
                  case "user":
                    return <UserChatMessage message={message} key={message.id} />;
                  case "error":
                    return <ErrorChatMessage message={message} key={message.id} />;
                  default:
                    return (
                      <AgentChatMessage
                        key={message.id}
                        message={message}
                        isStreaming={isStreaming && i === messages.length - 1}
                      />
                    );
                }
              })}
              {extras && extras.length > 0 && <AgentChatExtras extras={extras} onSend={send} />}
              {hasPendingApprovals && <ToolApprovalCard requests={pendingApprovals} onRespond={onRespondToApproval} />}
            </Stack>
          </Box>
        </ScrollArea>
        {userFeedback ? <Center mb={5}>{userFeedback}</Center> : undefined}
        <Box px="xs" pos="relative">
          <Transition mounted={showScrollToBottom} transition="pop" enterDelay={200}>
            {transitionStyles => (
              <Center pos="absolute" left={0} right={0} top={-60}>
                <ActionIcon
                  style={{
                    ...transitionStyles,
                    boxShadow: "var(--mantine-shadow-lg)",
                    border: "solid 1px var(--mantine-color-default-border)"
                  }}
                  radius="xl"
                  onClick={scrollToBottom}
                  bg="var(--mantine-color-default)"
                >
                  <Icon name="arrow-down" size="0.8rem" color="dimmed" />
                </ActionIcon>
              </Center>
            )}
          </Transition>

          {researchStatus}

          <ChatBox
            ref={chatInputRef}
            onSubmit={handleOnSubmit}
            onCancel={stop}
            disabled={disabled}
            isUploading={isUploading}
            isStreaming={isStreaming}
            isStopping={isStopping}
            isFileUploadEnabled={isFileUploadEnabled}
            isDeepResearchEnabled={isDeepResearchEnabled}
            selectedFiles={isFileUploadEnabled ? selectedFiles : undefined}
            onFilesChange={isFileUploadEnabled ? onFilesChange : undefined}
            selectedReports={isDeepResearchEnabled ? selectedReports : undefined}
            onReportsChange={isDeepResearchEnabled ? onReportsChange : undefined}
            committedDocuments={committedDocuments}
            onAttachClick={handleAttachClick}
            onAttachReportClick={isDeepResearchEnabled ? handleAttachDeepResearchReportClick : undefined}
            inputSuggestion={inputSuggestion}
            composerSeed={composerSeed}
            onComposerSeedConsumed={onComposerSeedConsumed}
            textareaProps={{
              placeholder: defaultPlaceholder,
              minRows: 2,
              maxRows: 6
            }}
          />
          <Center>
            <Group gap="xs" mt={7} align="center">
              <Text c="dimmed" size="xs">
                AI can make mistakes. Verify important information.
              </Text>
            </Group>
          </Center>
        </Box>
      </Stack>
      {isFileUploadEnabled && isDragging && <ChatDropOverlay blockedReason={dropBlockedReason} />}
    </Box>
  );
}
